/**
 * PositionFilters component for filtering positions.
 */

import { Card, CardContent, Input, Select, Button } from '@/components/ui';
import { DateRangePicker } from './DateRangePicker';
import type { PositionFilterState } from '@/types';

interface PositionFiltersProps {
  /** Current filter state */
  filters: PositionFilterState;
  /** Callback to update a filter value */
  onFilterChange: <K extends keyof PositionFilterState>(
    key: K,
    value: PositionFilterState[K]
  ) => void;
  /** Callback to clear all filters */
  onClear: () => void;
  /** Whether any filters are active */
  hasActiveFilters?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Positions' },
  { value: 'ACTIVE', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
];

const DATE_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'closed_at', label: 'Closed Date' },
];

/**
 * Filter controls for the positions page.
 * Includes symbol search, status filter, and date range filter.
 */
export function PositionFilters({
  filters,
  onFilterChange,
  onClear,
  hasActiveFilters = false,
}: PositionFiltersProps) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Symbol filter */}
          <div className="w-full sm:w-48">
            <Input
              label="Symbol"
              placeholder="Filter by symbol..."
              value={filters.symbol}
              onChange={(e) => onFilterChange('symbol', e.target.value)}
              aria-label="Filter positions by symbol"
            />
          </div>

          {/* Status filter */}
          <div className="w-full sm:w-40">
            <Select
              label="Status"
              value={filters.status}
              onChange={(e) =>
                onFilterChange(
                  'status',
                  e.target.value as PositionFilterState['status']
                )
              }
              options={STATUS_OPTIONS}
              aria-label="Filter by position status"
            />
          </div>

          {/* Date field selector */}
          <div className="w-full sm:w-40">
            <Select
              label="Date Field"
              value={filters.dateField}
              onChange={(e) =>
                onFilterChange(
                  'dateField',
                  e.target.value as PositionFilterState['dateField']
                )
              }
              options={DATE_FIELD_OPTIONS}
              aria-label="Select date field to filter"
            />
          </div>

          {/* Date range */}
          <div className="w-full sm:w-auto">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Date Range
            </label>
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onStartChange={(date) => onFilterChange('startDate', date)}
              onEndChange={(date) => onFilterChange('endDate', date)}
            />
          </div>

          {/* Clear filters button */}
          {hasActiveFilters && (
            <div className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={onClear}
                className="mt-auto"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
